import { isValidDateKey } from '@/lib/date-range-search';
import type { ReportRange } from './reportRange';

/**
 * What the retired Call Report left in the URL. It wrote its period with
 * `replaceDateRangeSearch` (see `@/lib/date-range-search`), so a bookmark taken
 * from that page carries `?range=` and, for a custom window, `from`/`to`.
 */
export type LegacyCallReportSearch = {
  range?: string;
  from?: string;
  to?: string;
};

export type ReportRedirectSearch = {
  range?: ReportRange;
  from?: string;
  to?: string;
};

/**
 * Translate an old Call Report URL into the Action Needed report's search.
 *
 * Both pages spell the period `range`, but they do not offer the same windows,
 * so only the ones that mean the same thing on both pages carry across:
 * `7d`, `30d`, `mtd` (the report calls it `month`, same start-of-month-to-now)
 * and a `custom` window with a valid pair of day keys.
 *
 * `today`, `week` and `ytd` have no counterpart. They are dropped rather than
 * rounded to the nearest window — landing on the report's own default period is
 * obvious, landing on a period the user did not pick is not.
 */
export function reportSearchFromLegacyCallReport(
  search: LegacyCallReportSearch,
): ReportRedirectSearch {
  if (search.range === '7d') return { range: '7d' };
  if (search.range === '30d') return { range: '30d' };
  if (search.range === 'mtd') return { range: 'month' };
  if (search.range === 'custom') {
    const { from, to } = search;
    // The same bar the old page applied before honouring the window, down to
    // rejecting a date that never happened. A half-written, impossible or
    // reversed pair is unusable: the report parses the bad side to nothing and
    // is left showing — and exporting — a range with one open end.
    if (from && to && isValidDateKey(from) && isValidDateKey(to) && from <= to) {
      return { range: 'custom', from, to };
    }
  }
  return {};
}
